import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { AppLayout } from 'components/AppLayout'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { Pill } from 'components/ManagementCard'
import { Snackbar } from 'components/Snackbar'
import { pageCardClasses } from 'components/AppLayout/styles'
import { EMPTY_VALUE } from 'constants/index'
import { routes } from 'constants/routes'
import { commonStrings, termsManagementStrings } from 'constants/strings'
import { toErrorMessage } from 'hooks/utils'
import {
  useTermsPolicy,
  useTermsVersions,
  useUpdateTermsEnforcement,
} from 'hooks/useTermsAdmin'
import type { TTermsPolicy, TTermsVersionListItem } from 'types/terms'
import { formatDateTime } from 'utils'

const strings = termsManagementStrings

export const buildTermsVersionPath = (versionId: number) =>
  routes.TERMS_MANAGEMENT_DETAIL.replace(':versionId', String(versionId))

function versionStatusPill(version: TTermsVersionListItem) {
  if (version.isActive) {
    return <Pill tone="success">{strings.versions.status.active}</Pill>
  }
  if (version.status === 'PUBLISHED') {
    return <Pill tone="neutral">{strings.versions.status.published}</Pill>
  }
  return <Pill tone="warning">{strings.versions.status.draft}</Pill>
}

function PolicySummary({ policy }: { policy: TTermsPolicy }) {
  const updateEnforcement = useUpdateTermsEnforcement()
  // 確認する操作は開いた時点で固定し、閉じた後も保持する
  // （保存後の再取得で、閉じるアニメーション中の文言が反対の操作に変わらないように）
  const [target, setTarget] = useState<{ enabled: boolean; revision: number }>()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [message, setMessage] = useState<string | undefined>()
  const nextEnabled = !policy.enforcementEnabled
  const canEnable = policy.activeVersion !== null
  const confirmEnabled = target?.enabled ?? nextEnabled

  const handleConfirm = async () => {
    if (!target) return
    try {
      await updateEnforcement.mutateAsync({
        enabled: target.enabled,
        expectedRevision: target.revision,
      })
      setMessage(target.enabled ? strings.policy.enabled : strings.policy.disabled)
    } catch {
      // 拒否・revision 競合は updateEnforcement.error で表示し、最新の状態は取り直し済み
    } finally {
      setConfirmOpen(false)
    }
  }

  return (
    <section className="mt-6 grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-ink">{strings.policy.title}</h2>
      <dl className="grid gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-[11px] font-semibold text-muted">
            {strings.policy.activeVersion}
          </dt>
          <dd className="mt-1 text-sm text-ink">
            {policy.activeVersion
              ? `${policy.activeVersion.versionLabel}（${policy.activeVersion.title}）`
              : strings.policy.noActiveVersion}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold text-muted">
            {strings.policy.consentGeneration}
          </dt>
          <dd className="mt-1 text-sm text-ink">{policy.consentGeneration}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold text-muted">
            {strings.policy.enforcement}
          </dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink">
            <Pill tone={policy.enforcementEnabled ? 'success' : 'neutral'}>
              {policy.enforcementEnabled
                ? strings.policy.enforcementOn
                : strings.policy.enforcementOff}
            </Pill>
            {policy.enforcementUpdatedAt && (
              <span className="text-[11px] text-muted">
                {strings.policy.enforcementUpdated}:{' '}
                {formatDateTime(policy.enforcementUpdatedAt)}{' '}
                {policy.enforcementUpdatedByName ?? ''}
              </span>
            )}
          </dd>
        </div>
      </dl>
      {updateEnforcement.error && (
        <Alert>
          {toErrorMessage(
            updateEnforcement.error,
            strings.errors.updateEnforcementFailed,
          )}
        </Alert>
      )}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {!policy.enforcementEnabled && !canEnable && (
          <span className="text-xs text-muted">
            {strings.policy.enableUnavailable}
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant={policy.enforcementEnabled ? 'outline' : 'primary'}
          disabled={nextEnabled && !canEnable}
          onClick={() => {
            setTarget({ enabled: nextEnabled, revision: policy.revision })
            setConfirmOpen(true)
          }}
        >
          {policy.enforcementEnabled
            ? strings.policy.disable
            : strings.policy.enable}
        </Button>
      </div>
      <ConfirmModal
        open={confirmOpen}
        textTitle={
          confirmEnabled
            ? strings.policy.enableConfirmTitle
            : strings.policy.disableConfirmTitle
        }
        textMessage={
          confirmEnabled
            ? strings.policy.enableConfirmMessage
            : strings.policy.disableConfirmMessage
        }
        textCancel={commonStrings.cancel}
        textConfirm={
          confirmEnabled ? strings.policy.enable : strings.policy.disable
        }
        isLoading={updateEnforcement.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          void handleConfirm()
        }}
      />
      <Snackbar
        open={Boolean(message)}
        message={message ?? ''}
        variant="success"
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setMessage(undefined)}
      />
    </section>
  )
}

function VersionsTable({
  versions,
  loading,
}: {
  versions: TTermsVersionListItem[]
  loading: boolean
}) {
  const navigate = useNavigate()
  const headers = strings.versions.headers
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = <DataTableMessageRow colSpan={7} message={commonStrings.loading} />
  } else if (versions.length === 0) {
    body = <DataTableMessageRow colSpan={7} message={strings.versions.empty} />
  } else {
    body = versions.map((version) => (
      <tr key={version.id} className="border-t border-border/60 bg-white">
        <td className="px-2 py-3 text-left">
          <Button
            type="button"
            size="sm"
            variant={version.status === 'DRAFT' ? 'primary' : 'outline'}
            onClick={() => navigate(buildTermsVersionPath(version.id))}
          >
            {version.status === 'DRAFT'
              ? strings.actions.edit
              : strings.actions.view}
          </Button>
        </td>
        <td className="px-2 py-3">{versionStatusPill(version)}</td>
        <td className="px-2 py-3 text-sm font-semibold text-ink">
          {version.versionLabel}
        </td>
        <td className="px-2 py-3 text-sm text-ink">
          {version.serviceName} / {version.title}
        </td>
        <td className="px-2 py-3 text-sm text-ink">
          {version.consentGeneration ?? EMPTY_VALUE}
        </td>
        <td className="px-2 py-3 text-sm text-ink">
          {version.reconsentRequired === null
            ? EMPTY_VALUE
            : version.reconsentRequired
              ? strings.versions.reconsent.yes
              : strings.versions.reconsent.no}
        </td>
        <td className="px-2 py-3 text-xs text-ink">
          {version.publishedAt
            ? `${formatDateTime(version.publishedAt)} ${version.publishedByName ?? ''}`
            : EMPTY_VALUE}
        </td>
      </tr>
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[860px]"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left">{headers.actions}</th>
          <th className="px-2 py-3 text-left">{headers.status}</th>
          <th className="px-2 py-3 text-left">{headers.versionLabel}</th>
          <th className="px-2 py-3 text-left">{headers.title}</th>
          <th className="px-2 py-3 text-left">{headers.generation}</th>
          <th className="px-2 py-3 text-left">{headers.reconsent}</th>
          <th className="px-2 py-3 text-left">{headers.published}</th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

export default function TermsManagement() {
  const navigate = useNavigate()
  const policy = useTermsPolicy()
  const versions = useTermsVersions()
  const error = policy.error
    ? toErrorMessage(policy.error, strings.errors.loadPolicyFailed)
    : versions.error
      ? toErrorMessage(versions.error, strings.errors.loadVersionsFailed)
      : undefined

  return (
    <AppLayout>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">{strings.title}</h1>
            <p className="max-w-[720px] text-sm text-muted">
              {strings.description}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate(routes.TERMS_MANAGEMENT_CREATE)}
          >
            {strings.actions.createDraft}
          </Button>
        </header>

        {error && (
          <div className="mt-6">
            <Alert>{error}</Alert>
          </div>
        )}

        {policy.data && <PolicySummary policy={policy.data} />}

        <section className="mt-6 grid gap-3">
          <h2 className="text-sm font-semibold text-ink">
            {strings.versions.title}
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll">
            <VersionsTable
              versions={versions.data ?? []}
              loading={versions.isPending}
            />
          </div>
        </section>
      </section>
    </AppLayout>
  )
}
